/**
 * Icon Registry
 * 
 * Central registry mapping icon names to Font Awesome definitions.
 * This allows using icons by name without importing each icon individually.
 * 
 * Usage:
 *   <WingsIcon icon="heart" size="large" />
 *   instead of importing faHeart
 */

// Free Solid Icons
import {
  faHome,
  faUser,
  faEnvelope,
  faPhone,
  faCog,
  faCheck,
  faTimes,
  faPlus,
  faMinus,
  faEdit,
  faTrash,
  faSave,
  faDownload,
  faUpload,
  faHeart,
  faStar,
  faBookmark,
  faBell,
  faShoppingCart,
  faCalendar,
  faCamera,
  faFile,
  faFolder,
  faImage,
  faVideo,
  faMusic,
  faLock,
  faUnlock,
  faEye,
  faEyeSlash,
  faThumbsUp,
  faThumbsDown,
  faShare,
  faComment,
  faComments,
  faArrowRight,
  faArrowLeft,
  faArrowUp,
  faArrowDown,
  faChevronRight,
  faChevronLeft,
  faChevronUp,
  faChevronDown,
  faCircle,
  faSquare,
  faBars,
  faGripVertical,
  faEllipsisV,
  faEllipsisH,
  faSpinner,
  faCircleNotch,
  faSync,
  faRedo,
  faUndo,
  faRefresh,
  faRocket,
  faShield,
  faAward,
  faCrown,
  faGem,
  faMagic,
  faBolt,
  faFire,
  faSun,
  faMoon,
  faCloud,
  faCloudUploadAlt,
  faCloudDownloadAlt,
  faInfoCircle,
  faExclamation,
  faExclamationCircle,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faQuestionCircle,
  faPlusCircle,
  faMinusCircle,
  faPlayCircle,
  faPauseCircle,
  faStopCircle,
  faPlay,
  faPause,
  faStop,
  faForward,
  faBackward,
  faStepForward,
  faStepBackward,
  faFastForward,
  faFastBackward,
  faVolumeUp,
  faVolumeDown,
  faVolumeMute,
  faVolumeOff,
  faPrint,
  faCopy,
  faPaste,
  faCut,
  faClipboard,
  faLink,
  faUnlink,
  faExternalLinkAlt,
  faAnchor,
  faPaperclip,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
  faList,
  faListOl,
  faListUl,
  faTh,
  faThList,
  faThLarge,
  faTable,
  faColumns,
  faGrip,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faFont,
  faTextHeight,
  faTextWidth,
  faParagraph,
  faHeading,
  faQuoteRight,
  faQuoteLeft,
  faCode,
  faTerminal,
  faDatabase,
  faServer,
  faNetworkWired,
  faWifi,
  faSignal,
  faBatteryFull,
  faBatteryHalf,
  faBatteryEmpty,
  faPlug,
  faPowerOff,
  faClock,
  faStopwatch,
  faHourglass,
  faHistory,
  faMapMarker,
  faMapMarkerAlt,
  faMap,
  faGlobe,
  faCompass,
  faStreetView,
  faChartLine,
  faChartBar,
  faChartPie,
  faChartArea,
  faUsers,
  faUserPlus,
  faUserMinus,
  faUserCheck,
  faUserTimes,
  faUserCog,
  faUserShield,
  faBuilding,
  faIndustry,
  faSchool,
  faUniversity,
  faHospital,
  faBriefcase,
  faGraduationCap,
  faCertificate,
  faMedal,
  faTrophy,
  faFlag,
  faThumbtack,
  faHashtag,
  faAt,
  faPercent,
  faDollarSign,
  faEuroSign,
  faPoundSign,
  faYenSign,
  faRubleSign,
  faCreditCard,
  faMoneyBill,
  faMoneyBillWave,
  faCoins,
  faWallet,
  faShoppingBag,
  faGift,
  faTag,
  faTags,
  faBarcode,
  faQrcode,
  faBug,
  faWrench,
  faScrewdriver,
  faHammer,
  faTools,
  faCogs,
  faSliders,
  faAdjust,
  faPalette,
  faPaintBrush,
  faFillDrip,
  faPencilAlt,
  faMarker,
  faHighlighter,
  faEraser,
  faMobileAlt,
  faTablet,
  faTabletAlt,
  faLaptop,
  faDesktop,
  faKeyboard,
  faMouse,
  faPrint as faPrinter,
  faTv,
  faGamepad,
  faHeadphones,
  faMicrophone,
  faMicrophoneSlash,
  faRoad,
  faPen as faPenSolid,
  faBookReader,
} from '@fortawesome/free-solid-svg-icons'

// Free Regular Icons
import {
  faHeart as faHeartRegular,
  faStar as faStarRegular,
  faBookmark as faBookmarkRegular,
  faCircle as faCircleRegular,
  faSquare as faSquareRegular,
  faCheckCircle as faCheckCircleRegular,
  faTimesCircle as faTimesCircleRegular,
  faUser as faUserRegular,
  faCalendar as faCalendarRegular,
  faClock as faClockRegular,
  faComment as faCommentRegular,
  faComments as faCommentsRegular,
  faFile as faFileRegular,
  faFolder as faFolderRegular,
  faImage as faImageRegular,
  faBell as faBellRegular,
  faEnvelope as faEnvelopeRegular,
} from '@fortawesome/free-regular-svg-icons'

import { faMapMarked as faMapMarkedSolid } from '@fortawesome/pro-solid-svg-icons'

// Pro Regular Icons
import {
  faRepeat as faRepeatRegular,
  faPen as faPenRegular,
  faChevronDown as faChevronDownRegular,
  faShareAlt,
  faSearch as faSearchRegular,
} from "@fortawesome/pro-regular-svg-icons"

// Pro Light Icons
import {
  faThumbsUp as faThumbsUpLight,
  faThumbsDown as faThumbsDownLight,
  faMapMarked,
} from "@fortawesome/pro-light-svg-icons"

// Free Brands Icons
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faLinkedin,
  faGithub,
  faYoutube,
  faTiktok,
  faSnapchat,
  faPinterest,
  faReddit,
  faTwitch,
  faDiscord,
  faSlack,
  faMicrosoft,
  faApple,
  faGoogle,
  faAmazon,
  faReact,
  faNode,
  faNpm,
  faGitAlt,
  faDocker,
  faFigma,
  faSketch,
  faWordpress,
  faShopify,
  faStripe,
  faPaypal,
  faBitcoin,
  faEthereum,
  faAndroid,
  faChrome,
  faFirefox,
  faSafari,
  faEdge,
  faSpotify,
  faSoundcloud,
  faDropbox,
  faDribbble,
  faBehance,
  faMedium,
  faStackOverflow,
} from '@fortawesome/free-brands-svg-icons'

/**
 * Icon Registry
 * Maps simple string names to Font Awesome icon definitions
 */
export const iconRegistry = {
  // Common Actions
  'home': faHome,
  'user': faUser,
  'email': faEnvelope,
  'envelope': faEnvelope,
  'phone': faPhone,
  'search': faSearchRegular,
  'settings': faCog,
  'cog': faCog,
  'check': faCheck,
  'times': faTimes,
  'close': faTimes,
  'x': faTimes,
  'plus': faPlus,
  'add': faPlus,
  'minus': faMinus,
  'subtract': faMinus,
  'edit': faEdit,
  'pencil': faEdit,
  'pen-solid': faPenSolid,
  'pen-outline': faPenRegular,
  'trash': faTrash,
  'delete': faTrash,
  'save': faSave,
  'download': faDownload,
  'upload': faUpload,
  
  // Social & Favorites
  'heart': faHeart,
  'heart-outline': faHeartRegular,
  'star': faStar,
  'star-outline': faStarRegular,
  'bookmark': faBookmark,
  'bookmark-outline': faBookmarkRegular,
  'bell': faBell,
  'bell-outline': faBellRegular,
  'cart': faShoppingCart,
  'shopping-cart': faShoppingCart,
  
  // Documents & Media
  'calendar': faCalendar,
  'calendar-outline': faCalendarRegular,
  'camera': faCamera,
  'file': faFile,
  'file-outline': faFileRegular,
  'folder': faFolder,
  'folder-outline': faFolderRegular,
  'image': faImage,
  'image-outline': faImageRegular,
  'video': faVideo,
  'music': faMusic,
  
  // Security & Privacy
  'lock': faLock,
  'unlock': faUnlock,
  'eye': faEye,
  'eye-slash': faEyeSlash,
  'hide': faEyeSlash,
  'show': faEye,
  'shield': faShield,
  
  // Feedback & Ratings
  'thumbs-up': faThumbsUp,
  'thumbs-up-outline': faThumbsUpLight,
  'thumbs-down': faThumbsDown,
  'thumbs-down-outline': faThumbsDownLight,
  'share': faShare,
  'share-alt': faShareAlt,
  'comment': faComment,
  'comment-outline': faCommentRegular,
  'comments': faComments,
  'comments-outline': faCommentsRegular,
  
  // Navigation Arrows
  'arrow-right': faArrowRight,
  'arrow-left': faArrowLeft,
  'arrow-up': faArrowUp,
  'arrow-down': faArrowDown,
  'chevron-right': faChevronRight,
  'chevron-left': faChevronLeft,
  'chevron-up': faChevronUp,
  'chevron-down': faChevronDown,
  'chevron-down-regular': faChevronDownRegular,
  
  // Shapes
  'circle': faCircle,
  'circle-outline': faCircleRegular,
  'square': faSquare,
  'square-outline': faSquareRegular,
  
  // Menu & UI
  'menu': faBars,
  'bars': faBars,
  'grip': faGripVertical,
  'ellipsis-v': faEllipsisV,
  'ellipsis-h': faEllipsisH,
  'more-vertical': faEllipsisV,
  'more-horizontal': faEllipsisH,
  'repeat': faRepeatRegular,
  'career-plans': faMapMarked,
  'career-plans-solid': faMapMarkedSolid,
  
  // Loading & Refresh
  'spinner': faSpinner,
  'loading': faSpinner,
  'circle-notch': faCircleNotch,
  'sync': faSync,
  'refresh': faSync,
  'redo': faRedo,
  'undo': faUndo,
  
  // Status & Icons
  'rocket': faRocket,
  'award': faAward,
  'crown': faCrown,
  'gem': faGem,
  'magic': faMagic,
  'bolt': faBolt,
  'fire': faFire,
  'sun': faSun,
  'moon': faMoon,
  'cloud': faCloud,
  'cloud-upload': faCloudUploadAlt,
  'cloud-download': faCloudDownloadAlt,
  
  // Alerts & Notifications
  'info': faInfoCircle,
  'info-circle': faInfoCircle,
  'exclamation': faExclamation,
  'warning': faExclamationTriangle,
  'exclamation-triangle': faExclamationTriangle,
  'alert': faExclamationCircle,
  'exclamation-circle': faExclamationCircle,
  'success': faCheckCircle,
  'check-circle': faCheckCircle,
  'check-circle-outline': faCheckCircleRegular,
  'error': faTimesCircle,
  'times-circle': faTimesCircle,
  'times-circle-outline': faTimesCircleRegular,
  'question': faQuestionCircle,
  'question-circle': faQuestionCircle,
  'plus-circle': faPlusCircle,
  'minus-circle': faMinusCircle,
  
  // Media Controls
  'play': faPlay,
  'play-circle': faPlayCircle,
  'pause': faPause,
  'pause-circle': faPauseCircle,
  'stop': faStop,
  'stop-circle': faStopCircle,
  'forward': faForward,
  'backward': faBackward,
  'volume-up': faVolumeUp,
  'volume-down': faVolumeDown,
  'volume-mute': faVolumeMute,
  'volume-off': faVolumeOff,
  
  // Text & Editing
  'print': faPrint,
  'copy': faCopy,
  'paste': faPaste,
  'cut': faCut,
  'clipboard': faClipboard,
  'link': faLink,
  'unlink': faUnlink,
  'external-link': faExternalLinkAlt,
  'paperclip': faPaperclip,
  'attach': faPaperclip,
  
  // Sorting & Filtering
  'filter': faFilter,
  'sort': faSort,
  'sort-up': faSortUp,
  'sort-down': faSortDown,
  
  // Layout
  'list': faList,
  'list-ol': faListOl,
  'list-ul': faListUl,
  'grid': faTh,
  'th': faTh,
  'table': faTable,
  'columns': faColumns,
  
  // Text Formatting
  'align-left': faAlignLeft,
  'align-center': faAlignCenter,
  'align-right': faAlignRight,
  'align-justify': faAlignJustify,
  'bold': faBold,
  'italic': faItalic,
  'underline': faUnderline,
  'strikethrough': faStrikethrough,
  'font': faFont,
  'heading': faHeading,
  'quote': faQuoteRight,
  'quote-right': faQuoteRight,
  'quote-left': faQuoteLeft,
  'code': faCode,
  
  // Tech & Development
  'terminal': faTerminal,
  'database': faDatabase,
  'server': faServer,
  'network': faNetworkWired,
  'wifi': faWifi,
  'signal': faSignal,
  'battery-full': faBatteryFull,
  'battery-half': faBatteryHalf,
  'battery-empty': faBatteryEmpty,
  'power': faPowerOff,
  
  // Time
  'clock': faClock,
  'clock-outline': faClockRegular,
  'stopwatch': faStopwatch,
  'hourglass': faHourglass,
  'history': faHistory,
  
  // Location
  'map-marker': faMapMarkerAlt,
  'location': faMapMarkerAlt,
  'map': faMap,
  'globe': faGlobe,
  'compass': faCompass,
  
  // Charts & Analytics
  'chart-line': faChartLine,
  'chart-bar': faChartBar,
  'chart-pie': faChartPie,
  'analytics': faChartLine,
  
  // Users & People
  'users': faUsers,
  'user-plus': faUserPlus,
  'user-minus': faUserMinus,
  'user-check': faUserCheck,
  'user-times': faUserTimes,
  'user-cog': faUserCog,
  'user-shield': faUserShield,
  'user-outline': faUserRegular,
  
  // Buildings & Places
  'building': faBuilding,
  'school': faSchool,
  'university': faUniversity,
  'hospital': faHospital,
  'briefcase': faBriefcase,
  
  // Education & Achievement
  'graduation': faGraduationCap,
  'certificate': faCertificate,
  'medal': faMedal,
  'trophy': faTrophy,
  'book-reader': faBookReader,
  
  // Money & Commerce
  'credit-card': faCreditCard,
  'dollar': faDollarSign,
  'money': faMoneyBill,
  'coins': faCoins,
  'wallet': faWallet,
  'shopping-bag': faShoppingBag,
  'gift': faGift,
  'tag': faTag,
  'tags': faTags,
  'road': faRoad,
  
  // Tools
  'bug': faBug,
  'wrench': faWrench,
  'hammer': faHammer,
  'tools': faTools,
  'cogs': faCogs,
  'sliders': faSliders,
  'palette': faPalette,
  'paint-brush': faPaintBrush,
  'eraser': faEraser,
  
  // Devices
  'mobile': faMobileAlt,
  'tablet': faTablet,
  'laptop': faLaptop,
  'desktop': faDesktop,
  'keyboard': faKeyboard,
  'mouse': faMouse,
  'printer': faPrinter,
  'tv': faTv,
  'gamepad': faGamepad,
  'headphones': faHeadphones,
  'microphone': faMicrophone,
  'microphone-slash': faMicrophoneSlash,
  
  // Social Media Brands
  'facebook': faFacebook,
  'twitter': faTwitter,
  'instagram': faInstagram,
  'linkedin': faLinkedin,
  'github': faGithub,
  'youtube': faYoutube,
  'tiktok': faTiktok,
  'snapchat': faSnapchat,
  'pinterest': faPinterest,
  'reddit': faReddit,
  'twitch': faTwitch,
  'discord': faDiscord,
  'slack': faSlack,
  
  // Tech Brands
  'microsoft': faMicrosoft,
  'apple': faApple,
  'google': faGoogle,
  'amazon': faAmazon,
  'react': faReact,
  'node': faNode,
  'npm': faNpm,
  'git': faGitAlt,
  'docker': faDocker,
  'figma': faFigma,
  'sketch': faSketch,
  'wordpress': faWordpress,
  'shopify': faShopify,
  'stripe': faStripe,
  'paypal': faPaypal,
  
  // Crypto
  'bitcoin': faBitcoin,
  'ethereum': faEthereum,
  
  // Browsers
  'chrome': faChrome,
  'firefox': faFirefox,
  'safari': faSafari,
  'edge': faEdge,
  
  // Other Services
  'spotify': faSpotify,
  'soundcloud': faSoundcloud,
  'dropbox': faDropbox,
  'dribbble': faDribbble,
  'behance': faBehance,
  'medium': faMedium,
  'stackoverflow': faStackOverflow,
}

/**
 * Get icon by name or return the icon object if already provided
 * @param {string|object} iconNameOrObject - Icon name or Font Awesome icon object
 * @returns {object|null} Font Awesome icon definition or null if not found
 */
export const getIcon = (iconNameOrObject) => {
  // If it's already an icon object, return it
  if (typeof iconNameOrObject === 'object' && iconNameOrObject !== null) {
    return iconNameOrObject
  }
  
  // If it's a string, look it up in the registry
  if (typeof iconNameOrObject === 'string') {
    const icon = iconRegistry[iconNameOrObject.toLowerCase()]
    if (!icon) {
      console.warn(`WingsIcon: Icon "${iconNameOrObject}" not found in registry. Add it to iconRegistry.js or pass the Font Awesome icon object directly.`)
      return null
    }
    return icon
  }
  
  return null
}
